import React, { useEffect, useState } from 'react';
import { Calendar, FileText, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { getHomepageSectionML } from '../../services/api';

const CTASectionML = () => {
  const { language } = useLanguage();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCTAContent();
  }, [language]);

  const loadCTAContent = async () => {
    setLoading(true);
    const normalizedLang = ['pt','en','fr','ar','zh'].includes(language) ? language : (language.startsWith('pt') ? 'pt' : 'en');
    const result = await getHomepageSectionML('cta', normalizedLang);
    if (result.success) {
      setContent(result.data);
    }
    setLoading(false);
  };

  if (loading || !content) {
    return (
      <section className="py-20 bg-[#023E8A]">
        <div className="container mx-auto px-6 text-center">
          <div className="animate-pulse text-white">Loading...</div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 md:py-32 relative text-white overflow-hidden bg-[#0a0a0a]">
      {/* Background Image if available */}
      {content.background_image && (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${content.background_image})`,
              filter: `brightness(${(content.image_brightness || 100) / 100})`
            }}
          />
          <div
            className="absolute inset-0"
            style={{ backgroundColor: `rgba(0, 0, 0, ${(content.overlay_opacity || 40) / 100})` }}
          />
        </>
      )}
      {/* Fallback gradient */}
      {!content.background_image && (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-[#023E8A] to-[#00B4D8]" />
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }} />
          </div>
        </>
      )}

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Heading */}
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
            {content.heading}
          </h2>
          
          {/* Subheading */}
          <p className="text-xl md:text-2xl text-white/90 mb-12 leading-relaxed">
            {content.subheading}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            {content.button_primary && (
              <Link to={content.button_primary.link || '/contact'}>
                <Button
                  size="lg"
                  className="bg-white text-[#023E8A] hover:bg-gray-100 font-semibold px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                >
                  <Calendar className="mr-2 h-5 w-5" />
                  {content.button_primary.text}
                </Button>
              </Link>
            )}
            {content.button_secondary && (
              <Link to={content.button_secondary.link || '/projects'}>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#023E8A] font-semibold px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
                >
                  <FileText className="mr-2 h-5 w-5" />
                  {content.button_secondary.text}
                </Button>
              </Link>
            )}
          </div>

          {/* Trust Note */}
          {content.trust_note && (
            <div className="flex items-center justify-center gap-2 text-sm text-white/80">
              <ShieldCheck className="h-4 w-4" />
              <span>{content.trust_note}</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CTASectionML;
